import { useCallback, useState } from "react";
import classnames from "classnames";

import Button from "@/components/button/button";
import Icon from "@/components/icon/icon";
import dialogStyles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";

export type MapWidgetEditableTitleStrings = {
  untitled: string;
  nameLabel: string;
  confirmTitleAria: string;
  cancelEditingAria: string;
  editTitleAria: string;
};

type MapWidgetEditableTitleProps = {
  value: string | undefined;
  onChange: (title: string) => void;
  strings: MapWidgetEditableTitleStrings;
  /**
   * Shown instead of `strings.untitled` (and as the input placeholder) when the
   * value is empty, for editors that save an empty title as this fallback.
   */
  fallback?: string;
  /**
   * `click-to-edit` reveals the input behind a pencil and only commits on
   * confirm. `always-input` renders the field directly and commits on every
   * keystroke, for editors where the title is a required form field. `auto`
   * picks between them by whether the editor opened without a name yet:
   * naming a new widget, then editing an existing one. Enter on the create
   * field commits (and trims) a non-empty name and switches to click-to-edit
   * in the same session.
   */
  variant?: "click-to-edit" | "always-input" | "auto";
};

export function MapWidgetEditableTitle({
  value,
  onChange,
  strings,
  fallback,
  variant = "click-to-edit",
}: MapWidgetEditableTitleProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  // Read once: resolving `auto` against the live value would swap the field
  // out from under the user on their first keystroke, taking focus with it.
  const [openedUnnamed] = useState(() => !value?.trim());
  // Enter on the create field locks in click-to-edit without remounting.
  const [namedInSession, setNamedInSession] = useState(false);

  const resolvedVariant =
    variant === "auto"
      ? openedUnnamed && !namedInSession
        ? "always-input"
        : "click-to-edit"
      : variant;

  const handleStartEditingTitle = () => {
    setEditedTitle(value || "");
    setIsEditingTitle(true);
  };

  const handleConfirmTitle = () => {
    onChange(editedTitle);
    setIsEditingTitle(false);
  };

  const handleCancelTitle = () => {
    setEditedTitle("");
    setIsEditingTitle(false);
  };

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onChange(editedTitle);
        setIsEditingTitle(false);
      }
    },
    [editedTitle, onChange],
  );

  const onAlwaysInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const next = value?.trim() ?? "";
    if (!next) return;
    if (next !== value) onChange(next);
    setNamedInSession(true);
  };

  if (resolvedVariant === "always-input") {
    return (
      <div
        className={classnames(
          dialogStyles.titleDisplayRow,
          dialogStyles.titleRowStandalone,
        )}
      >
        <input
          type="text"
          className={classnames(dialogStyles.titleInput, dialogStyles.titleInputStandalone)}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          required
          aria-label={strings.nameLabel}
          placeholder={fallback || strings.nameLabel}
          onKeyDown={onAlwaysInputKeyDown}
        />
      </div>
    );
  }

  return isEditingTitle ? (
    <div className={dialogStyles.titleDisplayRow}>
      <input
        type="text"
        className={dialogStyles.titleInput}
        value={editedTitle}
        onChange={(e) => setEditedTitle(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus
        placeholder={fallback || strings.nameLabel}
      />
      <Button
        type="button"
        variant="ghost"
        className={dialogStyles.titleActionButton}
        onClick={handleConfirmTitle}
        aria-label={strings.confirmTitleAria}
      >
        <Icon name="check" size={16} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        className={dialogStyles.titleActionButton}
        onClick={handleCancelTitle}
        aria-label={strings.cancelEditingAria}
      >
        <Icon name="x" size={16} />
      </Button>
    </div>
  ) : (
    <div className={dialogStyles.titleDisplayRow}>
      <span className={dialogStyles.titleValue}>
        {value || fallback || strings.untitled}
      </span>
      <Button
        type="button"
        variant="ghost"
        className={dialogStyles.titleEditButton}
        onClick={handleStartEditingTitle}
        aria-label={strings.editTitleAria}
      >
        <Icon name="pencil" size={16} />
      </Button>
    </div>
  );
}
